import { ConfirmPhoneIcon } from 'assets/icons';
import { AxiosError } from 'axios';
import { FieldInput } from 'components/redesign/FieldInput';
import { FieldTextarea } from 'components/redesign/FieldTextArea';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { useTimer } from 'hooks/useTimer';
import { FC, useEffect, useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';
import { modalActions } from 'reduxStore/reducers/ModalSlice';
import { getExecutorSpecializationIds } from 'reduxStore/reducers/ProfileSlice/selectors/getExecutorSpecializationIds';
import { getFtsProfile } from 'reduxStore/reducers/ProfileSlice/selectors/getFtsProfile';
import { ProfileService } from 'services/ProfileService';
import { Button } from 'ui/redesign/Button';
import { ButtonVariant } from 'ui/redesign/Button/constants';
import { InfoBlock } from 'ui/redesign/InfoBlock';
import { InfoBlockVariant } from 'ui/redesign/InfoBlock/constants';
import { Input } from 'ui/redesign/Input';
import { CodeRequestTimeout } from '../CodeRequestTimeout';
import { confirmCodeRules, smsTimeout } from './constants';
import { ChangePhoneForm, ChangePhoneNumberFormProps, ConfirmPhoneError } from './types';
import { FtsStatusEnum } from 'services/FtsOrganizationService/types';

export const ChangePhoneNumberForm: FC<ChangePhoneNumberFormProps> = ({ currentPhoneNumber }) => {
  const ftsProfile = useAppSelector(getFtsProfile);
  const {
    control,
    handleSubmit,
    getValues,
    formState: { isSubmitting, isValid, errors },
    setValue,
    watch,
  } = useForm<ChangePhoneForm>({
    mode: 'onChange',
    defaultValues: {
      phoneNumber: currentPhoneNumber,
      confirmCode: '',
    },
  });
  const [isVerifying, setIsVerifying] = useState(false);
  const [isNotEqual, setIsNotEqual] = useState(false);
  const queryResultRef = useRef<ConfirmResponse | null>(null);
  const queryClient = useQueryClient();
  const [timer, resetTimer] = useTimer(0);
  const dispatch = useAppDispatch();
  const executorSpecializationIds = useAppSelector(getExecutorSpecializationIds);

  const { mutateAsync: sendConfirmCode } = useMutation(
    ['sendConfirmCode'],
    () =>
      ProfileService.sendPhoneConfirmCode({
        newPhoneNumber: getValues('phoneNumber').replaceAll(' ', ''),
      }),
    {
      onSuccess: (data) => {
        if (data.data.isSuccess) {
          setIsVerifying(true);
          queryResultRef.current = data.data;
          resetTimer(smsTimeout);
        }
        queryResultRef.current = data.data;
      },
    }
  );
  const { mutateAsync: resendConfirmCode } = useMutation(
    ['sendConfirmCode'],
    () =>
      ProfileService.resendPhoneConfirmCode({
        phoneNumber: getValues('phoneNumber').replaceAll(' ', ''),
      }),
    {
      onSuccess: (data) => {
        setIsVerifying(true);
        queryResultRef.current = data.data;
        resetTimer(smsTimeout);
      },
    }
  );
  const { mutateAsync: changePhoneNumber } = useMutation(
    ['changePhoneNumber'],
    () =>
      ProfileService.changePhoneNumber({
        newPhoneNumber: getValues('phoneNumber').replaceAll(' ', ''),
        code: getValues('confirmCode'),
      }),
    {
      onSuccess: (data) => {
        if (data.data.isSuccess) {
          queryClient.refetchQueries(['getProfile']);
          queryClient.refetchQueries(['getFtsProfile']);
          dispatch(modalActions.closeModal());
        } else {
          queryResultRef.current = data.data;
          setValue('confirmCode', '');
        }
      },
      onError: (error: AxiosError<ConfirmPhoneError>) => {
        if (error?.response) {
          queryResultRef.current = {
            isSuccess: false,
            message: error.response.data.errors.NewPhoneNumber[0],
          };
        }
      },
    }
  );

  const currentPhone = watch('phoneNumber').replace(/\D+/g, '');
  const profilePhone = currentPhoneNumber.replace(/\D+/g, '');
  const isPhoneEqual = currentPhone === profilePhone || currentPhone.length !== 11;

  const sendHandler: SubmitHandler<ChangePhoneForm> = async () => {
    if (
      (ftsProfile?.ftsStatus === FtsStatusEnum.IncorrectFtsPhone ||
        ftsProfile?.ftsStatus === FtsStatusEnum.IncorrectFtsData) &&
      getValues('phoneNumber').replaceAll(/\D/g, '') !== ftsProfile?.ftsPhoneNumber
    ) {
      setIsNotEqual(true);
      return;
    } else {
      await sendConfirmCode();
    }
  };

  const clickHandler = async () => {
    await resendConfirmCode();
  };

  const confirmHandler: SubmitHandler<ChangePhoneForm> = async () => {
    await changePhoneNumber();
  };

  useEffect(() => {
    setIsNotEqual(false);
  }, [currentPhone]);

  return (
    <div className="w-full p-4 sm:min-w-[600px] sm:p-0">
      <form
        onSubmit={handleSubmit(isVerifying ? confirmHandler : sendHandler)}
        className="flex flex-col gap-4"
      >
        <div className="mt-4 text-center">
          <ConfirmPhoneIcon />
        </div>
        {isVerifying ? (
          <div className="mt-2 mb-4 text-text-100">
            {queryResultRef.current?.isSuccess ? (
              <InfoBlock
                text={queryResultRef.current.message.split('.')[0]}
                description={queryResultRef.current.message.split('.')[1]}
              />
            ) : (
              queryResultRef.current && (
                <InfoBlock
                  text={queryResultRef.current.message.split('.')[0]}
                  description={queryResultRef.current.message.split('.')[1]}
                  variant={InfoBlockVariant.ERROR}
                />
              )
            )}
          </div>
        ) : (
          queryResultRef.current && (
            <InfoBlock
              text={queryResultRef.current.message.split('.')[0]}
              description={queryResultRef.current.message.split('.')[1]}
              variant={InfoBlockVariant.ERROR}
              className="mb-4"
            />
          )
        )}
        {isNotEqual && (
          <InfoBlock
            text={'Введен некорректный номер.'}
            description={'Необходимо указать номер телефона, как в ЛК ФНС.'}
            variant={InfoBlockVariant.ERROR}
            className="mb-4"
          />
        )}
        <FieldInput
          name="phoneNumber"
          control={control}
          label="Новый номер телефона"
          mask="+7 (999) 999-99-99"
          disabled={isVerifying}
        />
        {executorSpecializationIds?.includes(5) && (
          // (ftsProfile?.ftsStatus === FtsStatusEnum.IncorrectFtsPhone ||
          //   ftsProfile?.ftsStatus === FtsStatusEnum.IncorrectFtsData) &&
          <Input
            value={ftsProfile?.ftsPhoneNumber ? ftsProfile?.ftsPhoneNumber : undefined}
            name="phoneNumberFTS"
            label="Номер телефона в ЛК ФНС"
            mask="+7 (999) 999-99-99"
            readOnly
          />
        )}
        {isVerifying ? (
          <>
            <div className="mt-4">
              <FieldTextarea
                name="confirmCode"
                control={control}
                label="Код из смс"
                rules={isVerifying && confirmCodeRules}
                isClearable
                maxLength={6}
              />
              {errors && <div className="text-danger">{errors['confirmCode']?.message}</div>}
              {timer > 0 && <CodeRequestTimeout timer={timer} />}
            </div>
            <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end">
              <Button
                disabled={timer > 0}
                variant={ButtonVariant.GHOST}
                type="button"
                onClick={clickHandler}
                className="body-paragraphs-normal mb-1 w-full max-w-[640px] !text-primary-80 sm:w-[270px]"
              >
                ОТПРАВИТЬ ПОВТОРНО
              </Button>
              <Button
                disabled={!isValid || isSubmitting}
                type="submit"
                className="body-paragraphs-bold mb-1 w-full max-w-[640px] sm:w-[240px]"
              >
                ПОДТВЕРДИТЬ КОД
              </Button>
            </div>
          </>
        ) : (
          <div className="mt-2 text-right">
            <Button
              disabled={isPhoneEqual || isSubmitting}
              type="submit"
              className="body-paragraphs-bold mb-1 w-full max-w-[640px] sm:w-[330px]"
            >
              ОТПРАВИТЬ СМС С КОДОМ
            </Button>
          </div>
        )}
      </form>
    </div>
  );
};
